import React, { createContext, useContext, useState, useEffect } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { useCategories } from './CategoriesContext';

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

export const ProductsProvider = ({ children }) => {
  // Initialize products from localStorage or use default values
  const [products, setProducts] = useState(() => {
    const savedProducts = localStorage.getItem('facit_products');
    return savedProducts ? JSON.parse(savedProducts) : [
      { 
        id: 1, 
        name: 'Développement Site Web', 
        description: 'Création de site web responsive avec CMS',
        type: 'service',
        category: 'Services Informatiques',
        price: 250000,
        unit: 'forfait',
        sku: 'SRV-001',
        stock: null,
        status: 'active'
      },
      { 
        id: 2, 
        name: 'Ordinateur Portable Dell', 
        description: 'Dell Latitude 7420, i7, 16GB RAM, 512GB SSD',
        type: 'product',
        category: 'Matériel Informatique',
        price: 185000,
        unit: 'unité',
        sku: 'MAT-002',
        stock: 12,
        status: 'active'
      },
      { 
        id: 3, 
        name: 'Formation React.js', 
        description: 'Formation complète React.js - 40 heures',
        type: 'service',
        category: 'Formation',
        price: 75000,
        unit: 'session',
        sku: 'FOR-003',
        stock: null,
        status: 'active'
      },
      { 
        id: 4, 
        name: 'Serveur HP ProLiant', 
        description: 'Serveur HP ProLiant DL380 Gen10',
        type: 'product',
        category: 'Matériel Informatique',
        price: 450000,
        unit: 'unité',
        sku: 'MAT-004',
        stock: 3,
        status: 'active'
      },
      { 
        id: 5, 
        name: 'Audit Sécurité IT', 
        description: 'Audit complet de sécurité informatique',
        type: 'service',
        category: 'Consulting',
        price: 120000,
        unit: 'jour',
        sku: 'CON-005',
        stock: null,
        status: 'inactive'
      }
    ];
  });

  const { 
    incrementProductCount, 
    decrementProductCount, 
    getCategoryByName,
    getCategoryByPrefix
  } = useCategories();
  
  // Save products to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('facit_products', JSON.stringify(products));
  }, [products]);
  
  const addProduct = (product) => {
    // Generate SKU if not provided
    let productSku = product.sku;
    if (!productSku) {
      const category = getCategoryByName(product.category);
      const prefix = category?.prefix || product.type.toUpperCase().slice(0, 3);
      const number = String(products.length + 1).padStart(3, '0');
      productSku = `${prefix}-${number}`;
    }
    
    const newProduct = {
      id: Math.max(0, ...products.map(p => p.id)) + 1,
      ...product,
      sku: productSku
    };
    
    const updatedProducts = [...products, newProduct];
    setProducts(updatedProducts);
    
    // Update category product count
    incrementProductCount(product.category);
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.PRODUCT_CREATED, newProduct);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'products');
    
    return newProduct;
  };

  const updateProduct = (id, updatedProduct) => {
    const oldProduct = products.find(product => product.id === id);
    
    // If category changed, update category counts
    if (oldProduct && updatedProduct.category && oldProduct.category !== updatedProduct.category) {
      decrementProductCount(oldProduct.category);
      incrementProductCount(updatedProduct.category);
    }
    
    const newProducts = products.map(product => 
      product.id === id ? { ...product, ...updatedProduct } : product
    );
    
    setProducts(newProducts);
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.PRODUCT_UPDATED, {
      oldProduct,
      newProduct: newProducts.find(product => product.id === id)
    });
    
    // If stock changed, emit special event
    if (oldProduct && updatedProduct.stock !== undefined && oldProduct.stock !== updatedProduct.stock) {
      eventEmitter.emit(EVENT_TYPES.PRODUCT_STOCK_CHANGED, {
        productId: id,
        oldStock: oldProduct.stock,
        newStock: updatedProduct.stock
      });
    }
    
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'products');
  };

  const deleteProduct = (id) => {
    const productToDelete = products.find(product => product.id === id);
    const newProducts = products.filter(product => product.id !== id);
    
    if (productToDelete) {
      decrementProductCount(productToDelete.category);
    }
    
    setProducts(newProducts);
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.PRODUCT_DELETED, productToDelete);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'products');
  };

  const getProductById = (id) => {
    return products.find(prod => prod.id === id);
  };

  const getActiveProducts = () => {
    return products.filter(prod => prod.status === 'active');
  };

  const getProductsByType = (type) => {
    return products.filter(prod => prod.type === type);
  };

  const getProductsByCategory = (category) => {
    return products.filter(prod => prod.category === category);
  };

  const getStockValue = () => {
    return products
      .filter(p => p.type === 'product')
      .reduce((sum, p) => sum + (p.price * (p.stock || 0)), 0);
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        addProduct,
        updateProduct,
        deleteProduct,
        getProductById,
        getActiveProducts,
        getProductsByType,
        getProductsByCategory,
        getStockValue
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export default ProductsContext; 